/**
 * in-memory-storage.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

/**
 * {@link InMemoryStorage} provides an interface matching the generic Storage
 * web API, but that implements all actions in local JS objects. These objects
 * will not persist as functionally intended, but the polyfill prevents larger
 * app breakage in environments where LocalStorage is disabled or unavailable.
 */
export class InMemoryStorage implements Storage
{
  private _store : {[prop : string] : any} = {};
  private _keys : string[] = [];

  // We aren't supporting this access method, but need to declare it in order to
  // claim that we "correctly" implement the Storage interface.
  [directIndex : number] : string;
  [directKey : string] : any;

  /**
   * Get the number of entries currently stored.
   * @return {number}
   */
  public get length () : number
  {
    return this._keys.length;
  }

  /**
   * Erase all stored entries.
   */
  public clear () : void
  {
    this._keys = [];
    this._store = {};
  }

  /**
   * Get the nth key stored (though I don't think browser storage makes any
   * guarantee that they are saved in the order they are created?)
   * @param  {number} n
   * @return {string}
   */
  public key (n : number) : string
  {
    return this._keys[n];
  }

  /**
   * [has description]
   * @param  {string}  key [description]
   * @return {boolean}
   */
  private has (key : string) : boolean
  {
    return (this._keys.indexOf(key) > -1);
  }

  /**
   * Retrieve an entry by its key.
   * @param  {string} key
   * @return {any}
   */
  public getItem (key : string) : any
  {
    return this._store[key];
  }

  /**
   * Store a new key-value entry.
   * @param {string} key
   * @param {any}    value
   */
  public setItem (key : string, value : any) : void
  {
    if (!this.has(key))
    {
      this._keys.push(key);
    }
    this._store[key] = value;
  }

  /**
   * Remove the entry for a particular key.
   * @param {string} key
   */
  public removeItem (key : string) : void
  {
    const i : number = this._keys.indexOf(key);
    if (i === -1)
    {
      return;
    }
    this._keys.splice(i, 1);
    delete this._store[key];
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/support/APIs/in-memory-storage.ts